import React, { useEffect, useId, useRef, useState } from 'react';
import styled from 'styled-components';

/**
 * The Email Manager Dashboard's own small design system.
 *
 * ## Why this screen does not use the shared form components
 *
 * The dashboard design is specific in a way the shared library is not built for: filters are
 * label-and-value pills on one line, fields are a small grey caption above a plain bordered box,
 * and buttons come in three variants that carry icons. The shared `Input` renders its own label
 * layout and emoji affordances, `Button` renders `props.text` and cannot take an icon child, and
 * the shared `TextArea` binds a *document-level* keydown handler and dereferences `ref.current`
 * in its mount effect — it is built for the modal it was written for.
 *
 * Bending those into these shapes would mean fighting them at every call site. Plain semantic
 * elements styled once here are both closer to the design and less to go wrong: a `<select>` is
 * keyboard- and screen-reader-correct for free, which a div-based dropdown is not.
 *
 * The upshot is that this screen imports no shared form component at all. That is a deliberate,
 * contained exception rather than a precedent: the primitives below are scoped to the Email
 * Manager, and any other screen should keep using `components/`.
 *
 * ## Colours
 *
 * Measured off the Figma frames. The indigo ramp is the dashboard's, and it is deliberately *not*
 * the email templates' palette: the emails measured to a neutral slate with a blue accent, because
 * they have to survive Outlook. Two different surfaces, two different constraints.
 */
export const T = {
  page: '#F8FAFC',
  card: '#FFFFFF',
  border: '#E5E7EB',
  borderSoft: '#F1F5F9',
  rowBorder: '#F3F4F6',
  panel: '#F8FAFC',

  heading: '#0F172A',
  body: '#374151',
  muted: '#6B7280',
  /*
    Raised from #9CA3AF, which measured about 2.6:1 on white and failed AA at every size.

    It is used for "Not scheduled", the em-dash result cell and 12px provider ids — small text
    carrying real information, which is the worst place to put the lightest grey in the palette.
    #6B7280 measures about 4.8:1 and still reads as recessive beside `muted`.
  */
  faint: '#6B7280',

  primary: '#4F46E5',
  primaryHover: '#4338CA',
  primarySoft: '#EEF2FF',
  primaryBorder: '#C7D2FE',

  green: '#166534',
  greenSoft: '#DCFCE7',
  amber: '#92400E',
  amberSoft: '#FEF3C7',
  red: '#991B1B',
  redSoft: '#FEE2E2',
  redBorder: '#FCA5A5',
  blue: '#3730A3',
  blueSoft: '#E0E7FF',
  slate: '#475569',
  slateSoft: '#F1F5F9',

  /*
    The one addition, rather than another literal: an off switch's track.

    `slateSoft` is too pale to read as a track against a white card and `slate` is too dark to read
    as "off", so the toggle had `#CBD5E1` inline — the only mid-grey on the screen and the only
    place it appeared. Naming it is what stops the next control needing its own.
  */
  track: '#CBD5E1'
};

/* ------------------------------------------------------------------ icons */

/**
 * Inline SVGs rather than the shared `Icon` component.
 *
 * `Icon` is driven by a `type` string, and a wrong one renders nothing at all — a silent blank
 * where the design has a glyph. These are the design's own icons, at the one size the dashboard
 * uses, and a typo here is a build error instead.
 */
const Svg = ({ children, size = 18, stroke = 'currentColor', fill = 'none' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={fill}
    stroke={stroke}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

export const Icons = {
  /*
    A link leaving for another page, rather than a chain link.

    The arrow is what distinguishes "this opens somewhere else" from "this is a hyperlink", and the
    preview pane's affordance needs the first meaning: it leaves the composer for the banner page.
  */
  External: (p) => (
    <Svg {...p}>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
    </Svg>
  ),
  Users: (p) => (
    <Svg {...p}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </Svg>
  ),
  Mail: (p) => (
    <Svg {...p}>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 6L2 7" />
    </Svg>
  ),
  Pencil: (p) => (
    <Svg {...p}>
      <path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
    </Svg>
  ),
  Layers: (p) => (
    <Svg {...p}>
      <path d="m12 2 9 5-9 5-9-5 9-5Z" />
      <path d="m3 12 9 5 9-5M3 17l9 5 9-5" />
    </Svg>
  ),
  Calendar: (p) => (
    <Svg {...p}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </Svg>
  ),
  Chart: (p) => (
    <Svg {...p}>
      <path d="M3 3v18h18" />
      <path d="M7 15v3M12 9v9M17 5v13" />
    </Svg>
  ),
  Shield: (p) => (
    <Svg {...p}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
      <path d="m9 12 2 2 4-4" />
    </Svg>
  ),
  Download: (p) => (
    <Svg {...p}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="m7 10 5 5 5-5M12 15V3" />
    </Svg>
  ),
  Upload: (p) => (
    <Svg {...p}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <path d="m17 8-5-5-5 5M12 3v12" />
    </Svg>
  ),
  Gear: (p) => (
    <Svg {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6 1.65 1.65 0 0 0 10 3.09V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9v0c.24.58.78.98 1.41 1H21a2 2 0 1 1 0 4h-.09c-.63.02-1.17.42-1.51 1Z" />
    </Svg>
  ),
  Plus: (p) => (
    <Svg {...p}>
      <path d="M12 5v14M5 12h14" />
    </Svg>
  ),
  Copy: (p) => (
    <Svg {...p}>
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </Svg>
  ),
  Monitor: (p) => (
    <Svg {...p}>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </Svg>
  ),
  Phone: (p) => (
    <Svg {...p}>
      <rect x="5" y="2" width="14" height="20" rx="2" />
      <path d="M12 18h.01" />
    </Svg>
  ),
  File: (p) => (
    <Svg {...p}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <path d="M14 2v6h6" />
    </Svg>
  ),
  Check: (p) => (
    <Svg {...p}>
      <path d="m20 6L9 17l-5-5" />
    </Svg>
  ),
  Sliders: (p) => (
    <Svg {...p}>
      <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" />
    </Svg>
  ),
  Refresh: (p) => (
    <Svg {...p}>
      <path d="M21 2v6h-6M3 22v-6h6" />
      <path d="M3.5 9a9 9 0 0 1 14.9-3.4L21 8M21 15a9 9 0 0 1-14.9 3.4L3 16" />
    </Svg>
  ),
  Chevron: (p) => (
    <Svg {...p}>
      <path d="m6 9 6 6 6-6" />
    </Svg>
  ),
  /*
    Added because Send Now had no glyph.

    `ScheduleCalendar` asked for an icon named Zap, which was never in this map — `Btn` looked it up,
    got `undefined`, and rendered nothing. So the one irreversible action in its row was the only
    button without an icon, silently. `Btn` now complains in development instead.
  */
  Send: (p) => (
    <Svg {...p}>
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </Svg>
  )
};

/* ------------------------------------------------------------------ layout */

export const Card = styled.section`
  background: ${T.card};
  border: 1px solid ${T.border};
  border-radius: 14px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const IconTile = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: ${T.primarySoft};
  color: ${T.primary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
`;

const HeadRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeadTitle = styled.h2`
  margin: 0;
  font-size: 19px;
  font-weight: 700;
  color: ${T.heading};
  letter-spacing: -0.01em;
`;

/** A section's title bar: icon tile, title, and whatever belongs on the right. */
export const SectionHead = ({ icon, title, right }) => {
  const Glyph = Icons[icon];

  return (
    <HeadRow>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <IconTile>{Glyph ? <Glyph size={17} /> : null}</IconTile>
        <HeadTitle>{title}</HeadTitle>
      </div>
      {right ? <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>{right}</div> : null}
    </HeadRow>
  );
};

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 12px;
`;

const BrandMark = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: ${T.primary};
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const BrandName = styled.span`
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.09em;
  color: ${T.primary};
`;

const BandTitle = styled.h1`
  margin: 0;
  font-size: 31px;
  line-height: 1.15;
  font-weight: 800;
  color: ${T.heading};
  letter-spacing: -0.02em;
`;

const Band = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  flex-wrap: wrap;
  padding-bottom: 20px;
  border-bottom: 1px solid ${T.border};
`;

/**
 * One of the design's three page bands.
 *
 * The Figma board is three frames, each opening with the logo, a title and its own action buttons —
 * they are one continuous page broken up for the board, not three pages. Reproduced as a repeated
 * band so the run of eight sections stays navigable without tabs.
 */
export const PageBand = ({ title, subtitle, actions, brand }) => (
  <Band>
    <div>
      {brand ? (
        <Brand>
          <BrandMark>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M13 2 4.5 13.5H11l-1 8.5 8.5-11.5H12l1-8.5Z" />
            </svg>
          </BrandMark>
          <BrandName>PLAYAZ4PLAYAZ</BrandName>
        </Brand>
      ) : null}
      <BandTitle>{title}</BandTitle>
      {subtitle ? (
        <p style={{ margin: '8px 0 0', fontSize: 14, color: T.muted }}>{subtitle}</p>
      ) : null}
    </div>
    {actions ? (
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {actions}
      </div>
    ) : null}
  </Band>
);

/** An uppercase caption over a group of controls — "AUDIENCE FILTERS" in the design. */
export const GroupLabel = styled.p`
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${T.muted};
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: ${(props) => props.cols || 'repeat(auto-fit, minmax(300px, 1fr))'};
  gap: ${(props) => props.gap || '16px'};
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

/* ------------------------------------------------------------------ buttons */

const buttonBase = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 10px;
  padding: 11px 17px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  white-space: nowrap;
  transition: background 120ms ease, border-color 120ms ease, color 120ms ease;

  /*
    A visible focus ring, because there was none.

    src/index.css removes the outline from every button and input app-wide, and nothing here put
    one back — so keyboard focus was invisible on every button on the screen. Text inputs *did*
    keep a ring, which made the screen look accessible right up to the point someone pressed Tab.

    :focus-visible rather than :focus, so a mouse click does not leave a ring behind.
  */
  &:focus-visible {
    outline: 2px solid ${T.primary};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const Solid = styled.button`
  ${buttonBase};
  background: ${T.primary};
  border: 1px solid ${T.primary};
  color: #fff;

  &:hover:enabled {
    background: ${T.primaryHover};
    border-color: ${T.primaryHover};
  }
`;

const Outline = styled.button`
  ${buttonBase};
  background: ${T.card};
  border: 1px solid ${T.border};
  color: ${T.heading};

  &:hover:enabled {
    border-color: ${T.faint};
    background: ${T.panel};
  }
`;

/*
  `T.red`, not `#dc2626`.

  Two reds were in use for the one destructive-and-error role: this button and the two KPI deltas
  on the reports page used `#dc2626`, while `T.red` (#991B1B) coloured every inline error message,
  the `Failed` panel and the red badge tone. Side by side they read as a deliberate distinction
  that means nothing — and the lighter one measures 4.8:1 on white against the token's 8.3:1.
*/
const Danger = styled.button`
  ${buttonBase};
  background: ${T.card};
  border: 1px solid ${T.redBorder};
  color: ${T.red};

  &:hover:enabled {
    background: ${T.redSoft};
  }
`;

/**
 * The dashboard's button, in the design's three variants.
 *
 * Local rather than the shared `Button` because these carry icons, and the shared one renders
 * `props.text` and ignores children entirely — an icon simply could not be passed to it. The red
 * outline variant (Archive) has no equivalent there either.
 */
export const Btn = ({ variant = 'solid', icon, children, ...rest }) => {
  const Glyph = icon ? Icons[icon] : null;

  /*
    An unknown icon name used to render nothing at all.

    That is how Send Now — the one irreversible control on the screen — ended up as the only
    button in its row without a glyph: the name Zap resolved to `undefined` and `Btn` shrugged.
    A missing icon is a typo, and a typo should be loud in development and harmless in production.
  */
  if (process.env.NODE_ENV !== 'production' && icon && !Glyph) {
    // eslint-disable-next-line no-console
    console.error(
      `Btn: no icon named "${icon}". Known icons: ${Object.keys(Icons).sort().join(', ')}.`
    );
  }
  const El = variant === 'outline' ? Outline : variant === 'danger' ? Danger : Solid;

  return (
    <El type="button" {...rest}>
      {Glyph ? <Glyph size={15} /> : null}
      {children}
    </El>
  );
};

/** A quiet icon-only square, for the copy button beside each template card. */
export const IconBtn = styled.button`
  ${buttonBase};
  padding: 0;
  width: 40px;
  height: 40px;
  flex: none;
  background: ${T.card};
  border: 1px solid ${T.border};
  color: ${T.muted};

  &:hover:enabled {
    color: ${T.primary};
    border-color: ${T.primaryBorder};
    background: ${T.primarySoft};
  }
`;

/** The small underlined action under a template card ("Desktop Preview"). */
export const LinkBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 600;
  color: ${T.primary};
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;

  &:disabled {
    color: ${T.faint};
    cursor: not-allowed;
    text-decoration: none;
  }
`;

/* ------------------------------------------------------------------ badges */

const TONES = {
  green: [T.greenSoft, T.green],
  amber: [T.amberSoft, T.amber],
  red: [T.redSoft, T.red],
  blue: [T.blueSoft, T.blue],
  indigo: [T.primarySoft, T.primary],
  slate: [T.slateSoft, T.slate]
};

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 999px;
  padding: ${(props) => (props.caps ? '4px 9px' : '4px 10px')};
  font-size: ${(props) => (props.caps ? '10px' : '12px')};
  font-weight: ${(props) => (props.caps ? 800 : 600)};
  letter-spacing: ${(props) => (props.caps ? '0.06em' : 0)};
  text-transform: ${(props) => (props.caps ? 'uppercase' : 'none')};
  white-space: nowrap;
  background: ${(props) => (TONES[props.tone] || TONES.slate)[0]};
  color: ${(props) => (TONES[props.tone] || TONES.slate)[1]};
`;

/**
 * A control the design shows that has no endpoint or no field behind it - a gap.
 *
 * Rendered as a visible marker rather than hidden or faked. A button that looks live and does
 * nothing is the worst of the three options: it invites an operator to believe a campaign was sent.
 * A missing one makes them hunt for it. This says which it is, in place.
 */
/**
 * A control the design shows that has no endpoint or no field behind it.
 *
 * It reads "Not available yet", not "Needs backend". Whether a thing has a backend is a fact about
 * our build, not about the operator's day: what they need to know is that this control cannot be
 * used and why. The badge is the same amber marker either way, and the reason is still in the
 * tooltip and the accessible name.
 *
 * **The reason is reachable by keyboard.** It lived in a `title` on a `<span>`, which needs a
 * hover to appear and is announced by almost nothing — so the one piece of information that made
 * the marker meaningful was available only to a sighted mouse user. It is now a focusable button
 * with the reason as its accessible description, and still visually a badge.
 */
export const NotWired = ({ note }) => (
  <Badge
    as="button"
    type="button"
    tone="amber"
    caps
    title={note}
    aria-label={`Not available yet: ${note}`}
    style={{ border: 0, cursor: 'help', font: 'inherit' }}
  >
    Not available yet
  </Badge>
);

/**
 * A field that is deliberately not editable here - a constraint, not a gap.
 *
 * Kept visually distinct from `NotWired` and much quieter, because amber reads as a warning and
 * these are working as intended: a template key that triggers reference, a greeting generated per
 * recipient, a footer that is global by design. Marking both the same way would put four warning
 * badges on a correctly-behaving editor and teach an operator to ignore the colour.
 */
export const ReadOnlyMark = ({ note }) => (
  <Badge tone="slate" caps title={note}>
    Read-only
  </Badge>
);

/* ------------------------------------------------------------------ fields */

const inputBase = `
  width: 100%;
  box-sizing: border-box;
  font-family: inherit;
  font-size: 14px;
  color: ${T.heading};
  background: ${T.card};
  border: 1px solid ${T.border};
  border-radius: 9px;
  padding: 11px 13px;

  &::placeholder { color: ${T.faint}; }

  &:focus {
    outline: none;
    border-color: ${T.primary};
    box-shadow: 0 0 0 3px ${T.primarySoft};
  }

  &:disabled {
    background: ${T.panel};
    color: ${T.muted};
    cursor: not-allowed;
  }

  &[aria-invalid='true'] {
    border-color: ${T.red};
  }

  &[aria-invalid='true']:focus {
    box-shadow: 0 0 0 3px ${T.redSoft};
  }
`;

export const TextInput = styled.input`
  ${inputBase};
`;

export const Textarea = styled.textarea`
  ${inputBase};
  resize: vertical;
  line-height: 1.55;
  min-height: ${(props) => props.minHeight || '92px'};
`;

export const Select = styled.select`
  ${inputBase};
  appearance: none;
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2' stroke-linecap='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 11px center;
  padding-right: 34px;
`;

const FieldLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${T.body};
`;

/**
 * Label above a control, with an optional hint below and a marker for gaps or fixed fields.
 *
 * **The label is associated with the control.** It was not: `FieldLabel` and `{children}` were
 * siblings with no `htmlFor` and no id, so every field on all six pages was announced as an
 * unlabelled edit box and clicking a label focused nothing. `htmlFor` appears nowhere else in this
 * repository either, so this is the convention starting rather than being restored.
 *
 * The id is generated and cloned onto the child, which means a caller writes nothing extra —
 * important, because there are dozens of these and a convention that needs remembering does not
 * hold. A child that already carries an id keeps it. The hint is wired through
 * `aria-describedby`, so it is read out too rather than being decoration for sighted users.
 *
 * **`required` and `error`.** A required field carries a red asterisk after its label and
 * `aria-required` on its control, so it is identifiable before anyone tries to proceed. `error` is
 * the message for a field that is empty or wrong: shown in red under the control, above the hint,
 * with the control's border turned red and `aria-invalid` set, and announced when it appears.
 *
 * **`action`.** A button that belongs to the control — "Find sends" beside an address box. It sits
 * in the control's own row and stretches to the control's height, and the hint and error go under
 * both. Laid out beside the whole field instead, it lined up with the bottom of the hint and sat
 * below the box it acts on.
 */
export const Field = ({
  label,
  hint,
  notWired,
  readOnlyNote,
  required,
  error,
  action,
  children,
  style
}) => {
  const generatedId = useId();
  const hintId = hint ? `${generatedId}-hint` : undefined;
  const errorId = error ? `${generatedId}-error` : undefined;

  const control = React.isValidElement(children)
    ? React.cloneElement(children, {
        id: children.props.id || generatedId,
        'aria-describedby':
          [children.props['aria-describedby'], errorId, hintId].filter(Boolean).join(' ') ||
          undefined,
        ...(required ? { 'aria-required': true } : {}),
        ...(error ? { 'aria-invalid': true } : {})
      })
    : children;

  const controlId = React.isValidElement(children) ? children.props.id || generatedId : undefined;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 7, minWidth: 0, ...style }}>
      {label ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <FieldLabel htmlFor={controlId}>
            {label}
            {notWired ? <NotWired note={notWired} /> : null}
            {readOnlyNote ? <ReadOnlyMark note={readOnlyNote} /> : null}
          </FieldLabel>
          {/* Outside the label, so the control's accessible name stays the label's own words. */}
          {required ? (
            <span aria-hidden="true" style={{ color: T.red, fontSize: 13, fontWeight: 700 }}>
              *
            </span>
          ) : null}
        </div>
      ) : null}
      {action ? (
        <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
          <div style={{ flex: 1, minWidth: 0 }}>{control}</div>
          {action}
        </div>
      ) : (
        control
      )}
      {error ? (
        <span
          id={errorId}
          role="alert"
          style={{ fontSize: 12, color: T.red, lineHeight: 1.5, fontWeight: 600 }}
        >
          {error}
        </span>
      ) : null}
      {hint ? (
        <span id={hintId} style={{ fontSize: 12, color: T.muted, lineHeight: 1.5 }}>
          {hint}
        </span>
      ) : null}
    </div>
  );
};

/* ------------------------------------------------------------------ pills */

const PillButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: ${T.card};
  border: 1px solid ${(props) => (props.$active ? T.primary : T.border)};
  border-radius: 999px;
  padding: 8px 14px;
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
  color: ${T.muted};

  &:hover:enabled {
    border-color: ${T.faint};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  strong {
    color: ${T.heading};
    font-weight: 700;
  }
`;

const Popover = styled.div`
  position: absolute;
  z-index: 30;
  top: calc(100% + 6px);
  left: 0;
  min-width: 240px;
  max-height: 300px;
  overflow-y: auto;
  background: ${T.card};
  border: 1px solid ${T.border};
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 13px;
  color: ${T.body};
  cursor: pointer;

  &:hover {
    background: ${T.primarySoft};
  }

  input {
    accent-color: ${T.primary};
    width: 15px;
    height: 15px;
    margin: 0;
  }
`;

/** Closes a popover on an outside click or Escape. Local, so no shared-hook signature to guess. */
const useDismiss = (open, close) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onDown = (event) => {
      if (ref.current && !ref.current.contains(event.target)) close();
    };
    const onKey = (event) => {
      if (event.key === 'Escape') close();
    };

    /*
      Focus goes into the popover, and comes back.

      It never moved: opening one left focus on the pill behind it, so a keyboard user had to Tab
      through the page to reach the checklist they had just opened — and Escape destroyed the
      focused node, dropping focus onto `<body>` with no way back.
    */
    const opener = document.activeElement;

    const firstFocusable = ref.current?.querySelector(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable?.focus?.();

    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);

      // Back to the pill that opened it, so Escape does not strand focus on <body>.
      if (opener?.focus) opener.focus();
    };
  }, [open, close]);

  return ref;
};

/**
 * A filter pill holding several values at once — the design's "Segment: Engaged/Casual/Dormant".
 *
 * The design shows every audience filter as one pill, but five of the eight take **arrays** on the
 * API. A single-select control cannot express those, so the pill summarises the selection and opens
 * a checklist. That keeps the design's one-line filter row and the API's real capability, which a
 * plain dropdown would have quietly thrown away.
 */
export const MultiPill = ({ label, options = [], selected = [], onChange, allLabel = 'All' }) => {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));

  const normalised = options.map((option) =>
    typeof option === 'string' ? { value: option, label: option } : option
  );

  const chosen = normalised.filter((option) => selected.includes(option.value));
  const summary =
    chosen.length === 0
      ? allLabel
      : chosen.length <= 3
        ? chosen.map((option) => option.label).join('/')
        : `${chosen.length} selected`;

  const toggle = (value) =>
    onChange(
      selected.includes(value) ? selected.filter((entry) => entry !== value) : [...selected, value]
    );

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <PillButton
        type="button"
        $active={chosen.length > 0}
        onClick={() => setOpen((was) => !was)}
        disabled={normalised.length === 0}
        aria-expanded={open}
        title={normalised.length === 0 ? 'No values in the current results' : undefined}
      >
        {label}: <strong>{normalised.length === 0 ? 'None available' : summary}</strong>
        <Icons.Chevron size={14} />
      </PillButton>

      {open ? (
        <Popover role="group" aria-label={label}>
          <CheckRow>
            <input type="checkbox" checked={selected.length === 0} onChange={() => onChange([])} />
            {allLabel}
          </CheckRow>
          {normalised.map((option) => (
            <CheckRow key={option.value}>
              <input
                type="checkbox"
                checked={selected.includes(option.value)}
                onChange={() => toggle(option.value)}
              />
              {option.label}
            </CheckRow>
          ))}
        </Popover>
      ) : null}
    </div>
  );
};

/**
 * A filter pill holding a date range - the design's "Last Active: Custom Date Range".
 *
 * A range needs two inputs and the design gives it one pill, so the pill summarises and the inputs
 * live in its popover. Four bare date fields on the filter row (which is what this was) is both
 * further from the design and harder to read: the row stops looking like a row of filters.
 */
export const RangePill = ({ label, range = {}, onChange, allLabel = 'All time' }) => {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));

  const summary =
    range.from && range.to
      ? `${range.from} to ${range.to}`
      : range.from
        ? `From ${range.from}`
        : range.to
          ? `Until ${range.to}`
          : allLabel;

  const set = (edge) => (event) => {
    const next = { ...range };
    if (event.target.value) next[edge] = event.target.value;
    else delete next[edge];
    onChange(Object.keys(next).length ? next : undefined);
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <PillButton
        type="button"
        $active={!!(range.from || range.to)}
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
      >
        {label}: <strong>{summary}</strong>
        <Icons.Chevron size={14} />
      </PillButton>

      {open ? (
        <Popover role="group" aria-label={label}>
          <Field label="From" style={{ padding: '4px 6px' }}>
            <TextInput type="date" value={range.from || ''} onChange={set('from')} />
          </Field>
          <Field label="To" style={{ padding: '4px 6px' }}>
            <TextInput type="date" value={range.to || ''} onChange={set('to')} />
          </Field>
          <button
            type="button"
            onClick={() => onChange(undefined)}
            style={{
              margin: '4px 6px 2px',
              border: 'none',
              background: 'none',
              padding: 0,
              font: 'inherit',
              fontSize: 12,
              color: T.primary,
              cursor: 'pointer',
              textAlign: 'left'
            }}
          >
            Clear this range
          </button>
        </Popover>
      ) : null}
    </div>
  );
};

/**
 * The bare `<select>` inside a pill.
 *
 * A styled component rather than an inline `style`, for two reasons. `appearance: none` leaves
 * Chrome's own `1px solid #767676` border in place, so the border has to be reset explicitly - and
 * an inline shorthand like `border: none` is dropped by jsdom's CSS serializer, which meant the
 * rendered-DOM snapshot used to check this screen showed a border the real browser does not draw.
 * Real CSS is honoured by both.
 */
const BareSelect = styled.select`
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: 0;
  outline: 0;
  background: none;
  font: inherit;
  font-weight: 700;
  color: ${T.heading};
  cursor: pointer;
  padding: 0 18px 0 0;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2' stroke-linecap='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right center;

  &:disabled {
    cursor: not-allowed;
    color: ${T.muted};
  }
`;

/** A filter pill holding one value. A native select, so keyboard and screen readers work. */
export const SelectPill = ({ label, value, options = [], onChange, disabled }) => (
  <PillButton as="div" $active={false} style={{ paddingRight: 10 }}>
    {label}:
    <BareSelect
      value={value ?? ''}
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
      aria-label={label}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </BareSelect>
  </PillButton>
);

export const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

/** A soft-tinted inset panel — the personalization-tag and guardrail boxes in the design. */
export const Inset = styled.div`
  background: ${(props) => (props.tint ? T.primarySoft : T.panel)};
  border: 1px solid ${(props) => (props.tint ? T.primaryBorder : T.border)};
  border-radius: 12px;
  padding: 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

/** A clickable token chip — "{First Name}", "+ Event Date". */
export const Tag = styled.button`
  background: ${T.card};
  border: 1px solid ${T.primaryBorder};
  border-radius: 7px;
  padding: 5px 9px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: ${T.primary};
  cursor: pointer;

  &:hover:enabled {
    background: ${T.primarySoft};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

/* ------------------------------------------------------------------ table */

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13.5px;

  th {
    text-align: left;
    padding: 12px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${T.muted};
    border-bottom: 1px solid ${T.border};
    white-space: nowrap;
  }

  td {
    padding: 15px 12px;
    color: ${T.body};
    border-bottom: 1px solid ${T.rowBorder};
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover td {
    background: ${T.panel};
  }
`;

/** Wide tables scroll inside themselves rather than making the page scroll sideways. */
export const TableScroll = styled.div`
  overflow-x: auto;
  margin: 0 -4px;
  padding: 0 4px;
`;

/* ------------------------------------------------------------------ kpi */

const KpiBox = styled.div`
  border: 1px solid ${T.border};
  border-radius: 12px;
  padding: 17px;
  background: ${T.card};
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-width: 0;
`;

/**
 * One headline metric.
 *
 * `value` may be `null`, which renders an em dash and the reason instead of a number. That case
 * exists because three of the design's four analytics cards measure things this system does not
 * track — and a `0.0%` under "Open Rate" does not read as "not measured", it reads as "nobody
 * opens our email". One is a gap; the other is a false and expensive conclusion.
 */
export const Kpi = ({ label, value, delta, deltaTone = 'green', sub, tone, notWired }) => (
  <KpiBox>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: T.muted
        }}
      >
        {label}
      </span>
      {delta ? (
        <Badge tone={deltaTone}>{delta}</Badge>
      ) : notWired ? (
        <NotWired note={notWired} />
      ) : null}
    </div>
    <span
      style={{
        fontSize: value === null ? 22 : 29,
        fontWeight: 800,
        letterSpacing: '-0.02em',
        color: value === null ? T.faint : tone || T.heading
      }}
    >
      {value === null ? '—' : value}
    </span>
    {sub ? <span style={{ fontSize: 12, color: T.muted, lineHeight: 1.5 }}>{sub}</span> : null}
  </KpiBox>
);

/** A ticked rule in the guardrails panel. */
/**
 * A rule that is either in force or not.
 *
 * **Different symbols, not just different colours.** It rendered a tick for both states and
 * changed only the colour — so "hard-bounced addresses are suppressed automatically — *active once
 * a provider is switched on*" displayed a tick whether or not it was true. That is a claim about
 * compliance behaviour conveyed by hue alone, invisible to anyone who cannot distinguish the two
 * greens and greys, and misleading to everyone else.
 */
export const Rule = ({ children, on = true }) => (
  <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 13, color: T.body }}>
    <span
      style={{ color: on ? T.green : T.muted, flex: 'none', marginTop: 1 }}
      role="img"
      aria-label={on ? 'In force' : 'Not in force'}
    >
      {on ? <Icons.Check size={15} /> : <Icons.Chevron size={15} />}
    </span>
    <span>{children}</span>
  </div>
);

/**
 * A region still waiting on its request.
 *
 * `Empty`'s other sibling. Without it a page in flight rendered its empty state — so the dashboard
 * flashed "No campaigns yet" on every single visit, and the audience table showed bare column
 * headers with no body. Both read as an answer when nothing had been asked yet.
 */
export const Loading = ({ title = 'Loading…' }) => (
  <Inset role="status" aria-live="polite">
    <span style={{ fontSize: 13.5, color: T.muted }}>{title}</span>
  </Inset>
);

/** An explanatory line at the foot of a section. */
export const Note = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.6;
  color: ${T.muted};
`;

/** A two-state switch, for the compliance force-apply toggle. */
const Track = styled.button`
  width: 42px;
  height: 23px;
  flex: none;
  border-radius: 999px;
  border: none;
  padding: 0;
  position: relative;
  cursor: pointer;
  background: ${(props) => (props.$on ? T.primary : T.track)};
  transition: background 140ms ease;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  span {
    position: absolute;
    top: 3px;
    left: ${(props) => (props.$on ? '22px' : '3px')};
    width: 17px;
    height: 17px;
    border-radius: 50%;
    background: #fff;
    transition: left 140ms ease;
  }
`;

export const Toggle = ({ on, onChange, label, disabled }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
    <Track
      type="button"
      $on={!!on}
      disabled={disabled}
      role="switch"
      aria-checked={!!on}
      aria-label={label}
      onClick={() => onChange(!on)}
    >
      <span />
    </Track>
    <span style={{ fontSize: 13.5, color: T.body }}>{label}</span>
  </div>
);

/** The Desktop/Mobile toggle above the live preview. */
const SegWrap = styled.div`
  display: inline-flex;
  background: ${T.panel};
  border: 1px solid ${T.border};
  border-radius: 9px;
  padding: 3px;
  gap: 3px;
`;

const SegBtn = styled.button`
  border: none;
  border-radius: 7px;
  padding: 6px 15px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: ${(props) => (props.$on ? T.card : 'transparent')};
  color: ${(props) => (props.$on ? T.heading : T.muted)};
  box-shadow: ${(props) => (props.$on ? '0 1px 2px rgba(15,23,42,0.08)' : 'none')};
`;

/**
 * A choice between mutually exclusive options.
 *
 * **A radio group, not a tab list.** It used `role="tablist"` / `role="tab"` / `aria-selected`
 * with no `tabpanel`, no `aria-controls` and no arrow-key handling — so assistive technology
 * announced tabs that led nowhere, and every option sat in the tab order instead of the group
 * taking one stop. Both consumers are choices, not navigation: the campaign status filter and the
 * preview variant switch.
 *
 * Arrow keys move between options and only the selected one is tabbable, which is what the radio
 * pattern requires and what a keyboard user expects.
 */
export const Segmented = ({ options, value, onChange, label }) => {
  const move = (delta) => {
    const index = options.findIndex((option) => option.value === value);
    const next = options[(index + delta + options.length) % options.length];
    if (next) onChange(next.value);
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      move(1);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      move(-1);
    }
  };

  return (
    <SegWrap role="radiogroup" aria-label={label} onKeyDown={onKeyDown}>
      {options.map((option) => (
        <SegBtn
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          // One tab stop for the group: Tab enters it, arrows move within it.
          tabIndex={value === option.value ? 0 : -1}
          $on={value === option.value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </SegBtn>
      ))}
    </SegWrap>
  );
};

/**
 * Campaign status, as colour and as a word.
 *
 * Here rather than in one of the two components that render it, because a status shown as "Queued"
 * on one page and "Scheduled" on another is the kind of drift nobody files a bug about and everyone
 * has to hold in their head.
 */
export const STATUS_TONE = {
  draft: 'slate',
  scheduled: 'amber',
  sending: 'blue',
  sent: 'green',
  cancelled: 'slate',
  failed: 'red',
  recurring: 'blue'
};

/**
 * Fan segment, as colour and as a word — beside campaign status, for the same reason.
 *
 * There were three of these: `AudienceFilters` said "Engaged", `AudienceTable` said "Engaged" with
 * a tone map of its own, and `CampaignBuilder` said "Engaged fans". So the filter chip, the table
 * column and the composer's audience select each named the same segment differently, and the
 * composer's wording was the one an operator read just before sending.
 *
 * `SEGMENT_LABEL` is the bare word, for a chip or a table cell. `segmentChoice` is the sentence
 * form the composer's select needs — kept as a function rather than a fourth map, so the two
 * cannot drift apart again.
 */
export const SEGMENT_TONE = { engaged: 'green', casual: 'blue', dormant: 'amber' };

export const SEGMENT_LABEL = { engaged: 'Engaged', casual: 'Casual', dormant: 'Dormant' };

export const segmentChoice = (segment) =>
  `Only ${(SEGMENT_LABEL[segment] || segment).toLowerCase()} fans`;

export const STATUS_LABEL = {
  draft: 'Draft',
  scheduled: 'Queued',
  sending: 'Sending',
  sent: 'Sent',
  cancelled: 'Cancelled',
  failed: 'Failed',
  // A series parent, which never sends itself — its occurrences are listed as their own rows.
  recurring: 'Repeating'
};

/**
 * An inline state where a whole section has nothing to show, with the reason.
 *
 * This sentence sat above the campaign-status maps, several declarations away from the component it
 * describes — the two were adjacent once and something was inserted between them.
 */
export const Empty = ({ title, children }) => (
  /*
    The container announces, rather than a hidden copy of the title.

    A visually-hidden duplicate would announce correctly and put the same string in the document
    twice, which breaks every `getByText` that names it — and it did. Marking the element that is
    already there says the same thing to a screen reader without lying to anything else.
  */
  <Inset role="status" aria-live="polite">
    <strong style={{ fontSize: 13.5, color: T.heading }}>{title}</strong>
    {children ? <Note>{children}</Note> : null}
  </Inset>
);

/**
 * A request that failed. `Empty`'s sibling, and deliberately not `Empty`.
 *
 * `lib/api.js` never rejects — it returns `false`, or `{ err }` when called `{ silent: true }` — so
 * `res?.data || []` renders a failure and an empty result identically. Every screen here did that,
 * and the consequences were not cosmetic: a failed template list told the operator their database
 * was unseeded and handed them a shell command, and a failed policy fetch left Settings reading
 * "Loading policy…" forever.
 *
 * So this exists to make the distinction visible, and it carries a retry, because the honest thing
 * to offer someone whose request failed is another attempt.
 */
export const Failed = ({ title = 'Could not load this', children, onRetry, busy }) => (
  <Inset style={{ borderColor: T.redSoft }} role="status" aria-live="polite">
    <strong style={{ fontSize: 13.5, color: T.red }}>{title}</strong>
    <Note>
      {children ||
        'Something went wrong fetching this. It is not that there is nothing to show — the request itself failed.'}
    </Note>
    {onRetry ? (
      <div style={{ marginTop: 10 }}>
        <Btn variant="outline" onClick={onRetry} disabled={busy} icon="Refresh">
          {busy ? 'Retrying…' : 'Try again'}
        </Btn>
      </div>
    ) : null}
  </Inset>
);
